import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IGamePlayer, IPlayerCalculations} from "data/utils/player.calculations";
import type {IContestsStore} from "data/stores/contests/contestsStore";

interface IParams {
	playerID: number;
	contestID: number;
	playerList: number[];
}

export interface IModalStatsController extends ViewController<IParams> {
	get isOpen(): boolean;
	get player(): IGamePlayer | undefined;
	get playerIdsList(): number[];
	get isFirst(): boolean;
	get isLast(): boolean;

	toNext: () => void;
	toPrev: () => void;
}

@injectable()
export class ModalStatsController implements IModalStatsController {
	@observable private _playerID = 0;
	@observable private _contestID = 0;
	@observable private _playerList: number[] = [];

	constructor(
		@inject(Bindings.PlayerCalculations)
		private readonly _playerCalculations: IPlayerCalculations,
		@inject(Bindings.ContestsStore) private readonly _contestStore: IContestsStore
	) {
		makeAutoObservable(this);
	}

	get isOpen(): boolean {
		return !!this._playerID;
	}

	init(param: IParams) {
		this._playerList = param.playerList;
		this._playerID = param.playerID;
		this._contestID = param.contestID;
	}

	onChange(param: IParams) {
		this.init(param);
	}

	get player() {
		return this._playerCalculations.getGamePlayerById(this._playerID, this._contestID);
	}

	get playerIdsList(): number[] {
		const clonePlayersList = [...this._playerList];
		return clonePlayersList.splice(-1).concat(clonePlayersList);
	}

	get isLast() {
		const list = this.playerIdsList;
		return list[list.length - 1] === this.player?.id;
	}

	get isFirst() {
		const list = this.playerIdsList;
		return list[0] === this.player?.id;
	}

	toNext = () => {
		if (this.isLast) {
			return;
		}
		const list = this.playerIdsList;
		const currentIndex = list.findIndex((it) => it === this.player?.id);
		this._playerID = list[currentIndex + 1];
	};

	toPrev = () => {
		if (this.isFirst) {
			return;
		}
		const list = this.playerIdsList;
		const currentIndex = list.findIndex((it) => it === this.player?.id);
		this._playerID = list[currentIndex - 1];
	};
}
